import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';

/**
 * MaintenanceGate — mirrors the Metriqs tag-domain portal lock.
 *
 * Polls the public, no-auth status endpoint every 15s AND whenever the browser
 * tab regains focus / becomes visible. The endpoint returns:
 *   { locked: bool, partial: bool }
 *
 *   • locked  -> FULL maintenance. A full-screen notice replaces the whole app
 *                (login included) with NOTHING mounted behind it.
 *   • partial -> "Sales & Approval" mode. The app still renders, but the ERP
 *                module is locked down the tree (see ErpLockedScreen, wired in
 *                App.js); only Notes / Tasks / Planner stay usable and chat is
 *                hidden (driven by the backend chat-availability flag).
 *
 * Precedence: locked ALWAYS wins over partial.
 *
 * Fail-open: if the status call errors or times out (2s), we treat the portal
 * as fully UNLOCKED so a transient network blip can never lock everyone out.
 * Lock / unlock is controlled ONLY by the super admin on metriqs.co.in — there
 * is no unlock UI here.
 */
const PORTAL_STATUS_URL = 'https://metriqs.co.in/api/public/portal-status';
const POLL_INTERVAL_MS = 15000;
const FETCH_TIMEOUT_MS = 2000;

const MaintenanceContext = createContext({ locked: false, partial: false });

export function useMaintenance() {
  return useContext(MaintenanceContext);
}

export default function MaintenanceGate({ children }) {
  // Start fully UNLOCKED so the app renders normally until a lock is seen.
  const [status, setStatus] = useState({ locked: false, partial: false });
  const mountedRef = useRef(true);

  const checkStatus = useCallback(async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(PORTAL_STATUS_URL, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      if (mountedRef.current) {
        setStatus({
          locked: !!(data && data.locked),
          partial: !!(data && data.partial),
        });
      }
    } catch (_err) {
      // Network blip / timeout / CORS / bad JSON -> fail OPEN (unlocked).
      if (mountedRef.current) setStatus({ locked: false, partial: false });
    } finally {
      clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    checkStatus(); // immediate check on mount

    const interval = setInterval(checkStatus, POLL_INTERVAL_MS);
    const onFocus = () => checkStatus();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkStatus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [checkStatus]);

  // FULL lock → replace everything with the maintenance screen.
  if (status.locked) return <MaintenanceScreen />;

  // Otherwise render the app, exposing {locked, partial} to the tree so the
  // ERP module can lock itself in partial mode.
  return (
    <MaintenanceContext.Provider value={status}>
      {children}
    </MaintenanceContext.Provider>
  );
}

/** Shared full-screen frame (plain light background, centred column). */
function FullScreen({ children, testid }) {
  return (
    <div
      data-testid={testid}
      role="alert"
      aria-live="assertive"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '24px',
        background: '#f5f7fa',
        color: '#1f2937',
      }}
    >
      {children}
    </div>
  );
}

function IconBubble({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '84px',
        height: '84px',
        borderRadius: '9999px',
        background: '#e8edf3',
        marginBottom: '24px',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Full-screen maintenance notice. Inline styles (not Tailwind) are used on
 * purpose so the overlay renders reliably even if app stylesheets fail to load,
 * and so nothing from the app can bleed through behind it.
 */
function MaintenanceScreen() {
  return (
    <FullScreen testid="maintenance-overlay">
      <IconBubble>
        <Lock size={38} strokeWidth={1.75} color="#64748b" />
      </IconBubble>
      <h1
        data-testid="maintenance-message"
        style={{ margin: 0, fontSize: '20px', lineHeight: 1.5, fontWeight: 600, maxWidth: '460px' }}
      >
        The site is under maintenance, we will be back shortly.
      </h1>
    </FullScreen>
  );
}

export { MaintenanceScreen };
